import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Copy, Check, ShieldCheck, QrCode } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, mediaUrl, pimg, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";

const Payment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
    api.get("/settings").then((r) => setSettings(r.data));
  }, [orderId]);

  const copyUpi = () => {
    navigator.clipboard.writeText(settings.upi_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const submit = async () => {
    if (!reference.trim()) { toast.error("Please enter your payment reference / UTR number"); return; }
    setSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/submit-payment`, { reference, method: "UPI" });
      clearCart();
      navigate(`/order-success?order=${orderId}`);
    } catch (e) { toast.error(errMsg(e)); setSubmitting(false); }
  };

  if (notFound)
    return <div className="mx-auto max-w-lg px-5 py-28 text-center"><p className="font-display text-3xl text-choco">Order not found</p></div>;
  if (!order || !settings) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-goldsoft" size={40} /></div>;

  const hasUpi = settings.upi_id || settings.payment_qr;

  return (
    <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14">
      <h1 className="font-display text-4xl font-bold text-choco">Complete Your Payment</h1>
      <p className="text-muted-foreground mt-2">Order <span className="font-semibold text-choco">{order.order_id}</span> · Amount due <span className="font-semibold text-choco">{formatINR(order.total)}</span></p>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <div className="bg-white rounded-2xl border border-[#EFEBE1] p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold text-choco mb-1">Pay via UPI / QR</h2>
          <p className="text-sm text-muted-foreground mb-5">{settings.payment_instructions}</p>

          {!hasUpi ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
              Online payment details haven't been configured yet. Please contact us at {settings.contact_phone} to complete your order.
            </div>
          ) : (
            <>
              {settings.payment_qr ? (
                <div className="flex justify-center">
                  <div className="bg-cream rounded-2xl p-4 border border-[#E5DFD3]">
                    <img data-testid="payment-qr" src={mediaUrl(settings.payment_qr)} alt="Payment QR" className="h-56 w-56 object-contain" />
                  </div>
                </div>
              ) : (
                <div className="flex justify-center text-muted-foreground py-6"><QrCode size={64} /></div>
              )}
              {settings.upi_id && (
                <div className="mt-5 flex items-center justify-between bg-cream rounded-xl px-4 py-3">
                  <div><p className="text-xs text-muted-foreground">UPI ID</p><p data-testid="payment-upi" className="font-semibold text-choco">{settings.upi_id}</p></div>
                  <button onClick={copyUpi} className="inline-flex items-center gap-1.5 text-sm text-choco hover:text-goldsoft">{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}</button>
                </div>
              )}
            </>
          )}

          <div className="mt-6">
            <label className="text-sm font-medium text-choco">Payment Reference / UTR Number</label>
            <Input data-testid="payment-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 4012 3456 7890" className="mt-1.5 h-12 bg-cream border-[#E5DFD3]" />
            <button data-testid="submit-payment-btn" onClick={submit} disabled={submitting} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark active:scale-95 transition-[background-color,transform] disabled:opacity-60">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} I've Paid — Place Order
            </button>
            <p className="text-xs text-muted-foreground mt-3">Your order will be confirmed once our team verifies the payment. No card details are ever stored.</p>
          </div>
        </div>

        <div>
          <div className="bg-white rounded-2xl border border-[#EFEBE1] p-6 sticky top-24">
            <h2 className="font-display text-2xl font-bold text-choco mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto no-scrollbar mb-4">
              {order.items.map((i, idx) => (
                <div key={`${i.product_id}-${i.customization || ""}-${idx}`} className="flex gap-3">
                  <img src={pimg(i.image)} alt={i.name} className="h-12 w-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-choco truncate">{i.name}</p><p className="text-xs text-muted-foreground">Qty {i.quantity}</p></div>
                  <span className="text-sm font-semibold text-choco">{formatINR(i.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#EFEBE1] pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold text-choco">{formatINR(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="font-semibold text-choco">{order.delivery_charge === 0 ? "FREE" : formatINR(order.delivery_charge)}</span></div>
              {(order.discount_amount || 0) > 0 && (
                <div data-testid="payment-discount" className="flex justify-between"><span className="text-green-700">Discount {order.coupon_code ? `(${order.coupon_code})` : ""}</span><span className="font-semibold text-green-700">− {formatINR(order.discount_amount)}</span></div>
              )}
              <div className="border-t border-[#EFEBE1] pt-2 flex justify-between items-center"><span className="font-semibold text-choco">Total</span><span className="font-display text-2xl font-bold text-choco">{formatINR(order.total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
